import { Button, Sheet } from '@gramas/ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { resolveBarcodeRemote } from '../../api/foods.js'
import type { LocalFood } from '../../db/dexie.js'
import { bindBarcode, findFoodByBarcode } from '../../db/repo.js'
import { GlitimeMark } from '../../glitime/Mark.js'
import { PersonaSpinner } from '../../glitime/PersonaSpinner.js'
import {
  configureBarcodeWasm,
  getDetector,
  type ScanHit,
  ScanVoter,
  toScanHit,
} from '../../scanner/barcode.js'
import { scanFeedback } from '../../scanner/feedback.js'
import { useCameraStream } from '../../scanner/useCameraStream.js'

/** Decode attempts per second. Faster spends battery without locking sooner. */
const DECODE_HZ = 8

type Phase =
  | { name: 'scanning' }
  | { name: 'looking'; code: string }
  | { name: 'missing'; code: string }

/**
 * The barcode reader, on its own, for choosing a product rather than logging it.
 *
 * The full scanner is a different instrument: it watches for nutrition panels,
 * holds an unknown code open for the label loop, and carries a mode rail. None
 * of that belongs in a picker, where the question is settled the moment a code
 * resolves — so this reads codes and nothing else.
 *
 * It exists because a recipe names the margarine you actually bought. Typing
 * "margarinas" offers the generic block the catalogue seeded; the packet in your
 * hand answers with the brand, the fat percentage and the numbers off its own
 * label, in one gesture and with no spelling involved.
 */
export function BarcodeScanSheet({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (food: LocalFood) => void
}) {
  const { videoRef, state: camera, start } = useCameraStream(open)
  const [phase, setPhase] = useState<Phase>({ name: 'scanning' })
  const voter = useRef(new ScanVoter())
  const busy = useRef(false)

  useEffect(() => {
    configureBarcodeWasm()
  }, [])

  useEffect(() => {
    if (!open) return
    voter.current.reset()
    busy.current = false
    setPhase({ name: 'scanning' })
  }, [open])

  const resolve = useCallback(
    async (hit: ScanHit) => {
      if (!hit.isGtin) return
      busy.current = true
      scanFeedback()
      setPhase({ name: 'looking', code: hit.value })
      try {
        // The phone's own bindings first: a product used before answers with no
        // network at all, which is most of what anyone scans twice.
        const local = await findFoodByBarcode(hit.value)
        if (local) {
          onPick(local)
          onOpenChange(false)
          return
        }
        if (navigator.onLine) {
          const remote = await resolveBarcodeRemote(hit.value)
          if (remote.food) {
            await bindBarcode(hit.value, hit.symbology, remote.food.id, true)
            onPick(remote.food)
            onOpenChange(false)
            return
          }
        }
        setPhase({ name: 'missing', code: hit.value })
      } catch {
        setPhase({ name: 'missing', code: hit.value })
      } finally {
        busy.current = false
      }
    },
    [onPick, onOpenChange],
  )

  useEffect(() => {
    if (!open || camera.status !== 'live') return
    let cancelled = false
    let timer: number | undefined

    const tick = async () => {
      const video = videoRef.current
      if (cancelled || !video || busy.current || video.readyState < 2) return
      try {
        const detector = await getDetector()
        const found = await detector.detect(video)
        const hit = found
          .map((code) => toScanHit(code.rawValue, code.format))
          .find((candidate): candidate is ScanHit => candidate != null)
        const accepted = voter.current.offer(hit ?? null)
        if (accepted && !cancelled) await resolve(accepted)
      } catch {
        // A dropped frame is not worth reporting; the next one is 125 ms away.
      }
    }

    timer = window.setInterval(() => void tick(), 1000 / DECODE_HZ)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [open, camera.status, videoRef, resolve])

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Nuskaityti brūkšninį kodą">
      <div className="flex flex-col gap-3 px-4 py-4">
        <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--radius-card)] bg-black">
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="size-full object-cover"
            aria-label="Kameros vaizdas"
          />
          {/* A window rather than a full frame: a barcode is wider than it is
              tall, and pointing at the strip is the whole instruction. */}
          <div className="pointer-events-none absolute inset-x-8 top-1/2 h-24 -translate-y-1/2 rounded-[var(--radius-control)] border-2 border-white/70" />
          {camera.status !== 'live' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              {camera.status === 'starting' && (
                <PersonaSpinner surface="scan" className="text-white" />
              )}
              {camera.status === 'denied' && (
                <p className="text-sm text-white">
                  {camera.error ??
                    'Kamera neleista. Įjunkite ją naršyklės nustatymuose ir bandykite dar kartą.'}
                </p>
              )}
              {(camera.status === 'idle' || camera.status === 'error') && (
                <Button onClick={() => void start()}>Įjungti kamerą</Button>
              )}
            </div>
          )}
        </div>

        {phase.name === 'scanning' && (
          <p className="text-sm text-mute">Nukreipkite į brūkšninį kodą ant pakuotės.</p>
        )}
        {phase.name === 'looking' && (
          <div className="flex items-center gap-2">
            <GlitimeMark size={20} alive station="busy" />
            <p className="tnum text-sm text-mute">Ieškoma {phase.code}…</p>
          </div>
        )}
        {phase.name === 'missing' && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-warn">
              Kodas <span className="tnum">{phase.code}</span> nerastas nei telefone, nei kataloge.
            </p>
            <p className="text-xs text-mute">
              Nuskaitykite etiketę skirtuke „Skenuoti“ — produktas bus įrašytas ir prisirišęs prie
              šio kodo, arba grįžkite ir suraskite pavadinimu.
            </p>
            <Button
              onClick={() => {
                voter.current.reset()
                setPhase({ name: 'scanning' })
              }}
            >
              Skenuoti dar kartą
            </Button>
          </div>
        )}
      </div>
    </Sheet>
  )
}
